/**
 * Supabase Sync Utilities
 * 
 * Provides shared utilities for the current DB-first sync pattern:
 * - Load data from Supabase on mount
 * - Write changes to Supabase
 * - Resolve workspace authority from demo mode or server-backed tenant context
 */

import { getSupabaseClient } from './supabaseClient';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getTenantContext, DEMO_WORKSPACE_ID } from './tenantContext';
import { notifyPrioritizationDashboardRelevantWrite } from './prioritizationDashboardRefreshEvents';
import { notifyWorkspaceContextChanged } from './workspaceContextEvents';
import { isSessionRunMode, runModeOfSavedConfig } from '../types/prioritization/session';

// ============================================================================
// Types
// ============================================================================

export interface SyncState {
  isLoading: boolean;
  isSyncing: boolean;
  lastSyncAt: Date | null;
  syncError: string | null;
  isOnline: boolean;
}

export interface SyncResult<T> {
  data: T | null;
  error: string | null;
}

// ============================================================================
// UUID Generation
// ============================================================================

/**
 * Generate a UUID v4 for database records
 * This is needed because Supabase expects UUID format for primary keys
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Safely get the Supabase client, returning null if not configured
 */
export function getSafeSupabaseClient(): SupabaseClient | null {
  try {
    return getSupabaseClient();
  } catch (error) {
    console.warn('[SupabaseSync] Supabase client not configured:', error);
    return null;
  }
}

/**
 * Check if Supabase is available
 */
export function isSupabaseAvailable(): boolean {
  return getSafeSupabaseClient() !== null;
}

/**
 * Whether there's a real authenticated Supabase session (a non-null auth.uid()).
 * Used to gate writes that must persist to a workspace — an anonymous session
 * is rejected by RLS, so the caller should prompt sign-in rather than create
 * rows that only live on the device. Returns false when Supabase isn't
 * configured (local/offline mode, where local-only writes are expected).
 */
export async function isAuthenticated(): Promise<boolean> {
  const client = getSafeSupabaseClient();
  if (!client) return false;
  try {
    const { data, error } = await client.auth.getSession();
    return !error && !!data?.session?.user;
  } catch {
    return false;
  }
}

/** Resolve auth.users.id from the live session (authoritative for RLS). */
export async function getAuthenticatedUserId(): Promise<string | null> {
  const client = getSafeSupabaseClient();
  if (!client) return null;
  try {
    const { data, error } = await client.auth.getSession();
    if (error || !data?.session?.user?.id) return null;
    return data.session.user.id;
  } catch {
    return null;
  }
}

/**
 * Convert camelCase to snake_case for database columns
 */
export function toSnakeCase(str: string): string {
  return str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}

/**
 * Convert snake_case to camelCase for TypeScript objects
 */
export function toCamelCase(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
}

/**
 * Transform object keys from camelCase to snake_case for database
 */
export function toDbFormat<T extends Record<string, unknown>>(obj: T): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const snakeKey = toSnakeCase(key);
    const value = obj[key];
    
    // Handle dates
    if (value instanceof Date) {
      result[snakeKey] = value.toISOString();
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      // Nested objects stay as JSONB
      result[snakeKey] = value;
    } else {
      result[snakeKey] = value;
    }
  }
  return result;
}

/**
 * Compatibility shim for cycle org scope until all environments have a
 * dedicated org_scope_node_id column on degap_cycles.
 */
function normalizeDegapCycleRecord(record: Record<string, unknown>): void {
  const orgScopeNodeId = record['org_scope_node_id'];
  if (typeof orgScopeNodeId !== 'string' || orgScopeNodeId.length === 0) {
    return;
  }

  const metadata =
    typeof record['metadata'] === 'object' && record['metadata'] !== null && !Array.isArray(record['metadata'])
      ? { ...(record['metadata'] as Record<string, unknown>) }
      : {};

  metadata.orgScopeNodeId = orgScopeNodeId;
  record['metadata'] = metadata;

  // Strip the top-level field so inserts/updates succeed before the DB column exists.
  delete record['org_scope_node_id'];
}

/**
 * Phase C-2 (canon 28 §6, migration 598): cycle Session Policy keys
 * (timingMode / entryMode / defaultFacilitatorId) live on session_policies
 * and are only synthesized into metadata.sessionDefaults by client-side
 * decoration. Strip them before any degap_cycles write so decorated values
 * are never re-persisted to the retired surface. Non-policy sessionDefaults
 * keys (resultResolution, defaultDurationMinutes, catchballEnabled) are real
 * cycle metadata and are preserved.
 */
function stripCyclePolicyKeysFromMetadata(record: Record<string, unknown>): void {
  const metadata = record['metadata'];
  if (typeof metadata !== 'object' || metadata === null || Array.isArray(metadata)) return;
  const defaults = (metadata as Record<string, unknown>)['sessionDefaults'];
  if (typeof defaults !== 'object' || defaults === null || Array.isArray(defaults)) return;
  const nextDefaults = { ...(defaults as Record<string, unknown>) };
  delete nextDefaults['timingMode'];
  delete nextDefaults['entryMode'];
  delete nextDefaults['defaultFacilitatorId'];
  const nextMetadata = { ...(metadata as Record<string, unknown>) };
  if (Object.keys(nextDefaults).length === 0) {
    delete nextMetadata['sessionDefaults'];
  } else {
    nextMetadata['sessionDefaults'] = nextDefaults;
  }
  record['metadata'] = nextMetadata;
}

/**
 * Phase C-2 (migration 598): the workspaces default_timing_mode /
 * default_entry_mode / default_facilitator_id columns were dropped; the
 * camelCase domain fields are decoration from session_policies. Strip them
 * from any workspaces write.
 */
function stripWorkspacePolicyColumns(record: Record<string, unknown>): void {
  delete record['default_timing_mode'];
  delete record['default_entry_mode'];
  delete record['default_facilitator_id'];
}

/**
 * Transform object keys from snake_case to camelCase for TypeScript
 */
export function fromDbFormat<T>(obj: Record<string, unknown>): T {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const camelKey = toCamelCase(key);
    const value = obj[key];
    
    // Handle date strings
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
      result[camelKey] = new Date(value);
    } else {
      result[camelKey] = value;
    }
  }
  return result as T;
}

// ============================================================================
// Generic CRUD Operations
// ============================================================================

/**
 * Check if we're in demo mode
 */
export function isInDemoMode(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem('peakPriorities_demoMode') === 'true';
}

/**
 * Check if strict workspace filtering is enabled
 * When enabled, data queries only return records for the exact workspace (no null workspace_id fallback)
 */
export function isStrictWorkspaceMode(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem('peakPriorities_strictWorkspace') === 'true';
}

/**
 * Enable or disable strict workspace filtering
 */
export function setStrictWorkspaceMode(enabled: boolean): void {
  if (typeof sessionStorage === 'undefined') return;
  if (enabled) {
    sessionStorage.setItem('peakPriorities_strictWorkspace', 'true');
  } else {
    sessionStorage.removeItem('peakPriorities_strictWorkspace');
  }
}

const CURRENT_WORKSPACE_STORAGE_KEY = 'peakPriorities_currentWorkspaceId';

/**
 * Get the current workspace ID for filtering queries and authority.
 * Stage 7 Slice 2: Authority and data filtering must use server/auth-backed context, not client storage.
 *
 * Resolution order:
 * 1. Demo mode: sessionStorage demo workspace id (special case; no auth).
 * 2. Authority: getTenantContext() — set by ensureWorkspaceContext() from Supabase auth + workspace_memberships (DB).
 * 3. No fallback to sessionStorage for non-demo; when tenant context is unset, returns null (fail honestly).
 *
 * Client storage (sessionStorage CURRENT_WORKSPACE_STORAGE_KEY) may still be written by tenant store as a UX hint
 * (e.g. workspace switcher); it is not read here for authority. Use ensureWorkspaceContext() at app init to set
 * tenant context from DB.
 */
export function getCurrentWorkspaceId(): string | null {
  if (typeof sessionStorage === 'undefined') return null;

  if (isInDemoMode()) {
    return sessionStorage.getItem('peakPriorities_demoWorkspaceId') || DEMO_WORKSPACE_ID;
  }

  const tenant = getTenantContext();
  if (tenant?.workspaceId) return tenant.workspaceId;

  // Fall back to the sessionStorage hint written by the tenant store so that workspace-scoped
  // fetches can use the correct workspace ID on page refresh, before ensureWorkspaceContext()
  // has had a chance to resolve and populate the module-level tenantContext variable.
  // Without this, fetches fired in the first useEffect cycle return empty (no workspace filter),
  // which briefly shows the zero-state screen before the real data loads (flicker).
  return sessionStorage.getItem(CURRENT_WORKSPACE_STORAGE_KEY) ?? null;
}

/**
 * Stage 7 Slice 2: Non-authoritative hint only. Call when the user explicitly switches workspace in UI
 * and the new workspace is known to be in the user's membership (e.g. from tenant store).
 * This keeps sessionStorage in sync for backward compatibility; authority remains getTenantContext().
 */
export function setCurrentWorkspaceIdHint(workspaceId: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  const previous = sessionStorage.getItem(CURRENT_WORKSPACE_STORAGE_KEY);
  if (workspaceId) {
    sessionStorage.setItem(CURRENT_WORKSPACE_STORAGE_KEY, workspaceId);
  } else {
    sessionStorage.removeItem(CURRENT_WORKSPACE_STORAGE_KEY);
  }
  // Workspace-scoped caches read through getCurrentWorkspaceId(); tell them the
  // answer changed. updateTenantContextWorkspace() is a no-op when no tenant
  // context exists yet, so the hint is sometimes the only signal that fires.
  if (previous !== (workspaceId ?? null)) notifyWorkspaceContextChanged(workspaceId);
}

/**
 * Fetch all records from a table
 * 
 * Options:
 * - filterByWorkspace: set to TRUE to enable workspace filtering (opt-in, not all tables have workspace_id)
 * - strictWorkspaceFilter: if true, only return records for the exact workspace (no null workspace_id)
 */
/** Tables that have no workspace_id column; we never apply workspace filter to avoid Postgres 42703. */
const TABLES_WITHOUT_WORKSPACE_ID = new Set([
  'item_clusters',
  'value_proposals',
  'accepted_values',
  'accepted_exceptions',
  'input_round_items',
  'input_round_participants',
]);

export async function fetchAll<T>(
  tableName: string,
  orderBy?: { column: string; ascending?: boolean },
  options?: { filterByWorkspace?: boolean; strictWorkspaceFilter?: boolean }
): Promise<SyncResult<T[]>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const workspaceId = getCurrentWorkspaceId();
    const requestedWorkspaceFilter = options?.filterByWorkspace === true;
    const wantsWorkspaceFilter =
      requestedWorkspaceFilter && !TABLES_WITHOUT_WORKSPACE_ID.has(tableName);

    // Default: see nothing. When workspace-scoped tables are requested but no workspace is selected, return empty.
    if (wantsWorkspaceFilter && !workspaceId) {
      console.log(`[SupabaseSync] fetchAll ${tableName}: no workspace selected, returning empty (default: see nothing)`);
      return { data: [] as T[], error: null };
    }

    let query = supabase.from(tableName).select('*');
    const shouldFilter = wantsWorkspaceFilter && !!workspaceId;

    if (shouldFilter) {
      // Default: include current workspace OR workspace_id IS NULL so legacy/unscoped data remains visible.
      // Pass strictWorkspaceFilter: true to only show rows for the exact workspace.
      const useStrictFilter = options?.strictWorkspaceFilter === true;
      if (useStrictFilter) {
        query = query.eq('workspace_id', workspaceId);
        console.log(`[SupabaseSync] STRICT filtering ${tableName} by workspace_id: ${workspaceId}`);
      } else {
        query = query.or(`workspace_id.eq.${workspaceId},workspace_id.is.null`);
        console.log(`[SupabaseSync] Filtering ${tableName} by workspace_id: ${workspaceId} (including null)`);
      }
    } else {
      console.log(`[SupabaseSync] No workspace filter applied to ${tableName}`);
    }

    if (orderBy) {
      query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
    }

    const { data, error } = await query;
    
    if (error) {
      console.error(`[SupabaseSync] Error fetching ${tableName}:`, error);
      return { data: null, error: error.message };
    }
    
    const transformed = (data || []).map(row => fromDbFormat<T>(row as Record<string, unknown>));
    return { data: transformed, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception fetching ${tableName}:`, error);
    return { data: null, error: message };
  }
}

/**
 * Fetch a single record by ID
 */
export async function fetchById<T>(
  tableName: string,
  id: string
): Promise<SyncResult<T>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('id', id)
      .single();
    
    if (error) {
      console.error(`[SupabaseSync] Error fetching ${tableName} by id:`, error);
      return { data: null, error: error.message };
    }
    
    return { data: fromDbFormat<T>(data as Record<string, unknown>), error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception fetching ${tableName} by id:`, error);
    return { data: null, error: message };
  }
}

/**
 * Write a session's run mode to the `run_mode` column from its `mode_config`,
 * so the two copies are one value (migration 1045 holds the same on the server).
 *
 * On insert the column always follows mode_config (its runMode, else the one
 * rule from the legacy pair). On update it follows only an explicit
 * mode_config.runMode: an update without one leaves the column as saved.
 * Exported for tests.
 */
export function promoteSessionRunMode(dbRecord: Record<string, unknown>, op: 'insert' | 'update'): void {
  const mc = dbRecord['mode_config'];
  if (typeof mc !== 'object' || mc === null || Array.isArray(mc)) return;
  const config = mc as Record<string, unknown>;
  if (op === 'update' && !isSessionRunMode(config['runMode'])) return;
  dbRecord['run_mode'] = runModeOfSavedConfig(config);
}

/**
 * The row insertRecord would write for `tableName`: camelCase → snake_case plus
 * the per-table column fixes and the current workspace_id. Exported so a
 * governed create RPC (e.g. create_cycle, mig 762) receives exactly the row the
 * direct insert used to send.
 */
export function toInsertRow<T extends object>(tableName: string, record: T): Record<string, unknown> {
  const dbRecord = toDbFormat(record as unknown as Record<string, unknown>);
  // item_pools: omit columns not in DB schema to avoid schema cache error
  if (tableName === 'item_pools') {
    delete dbRecord['comparison_set_id'];
    delete dbRecord['criteria_ids'];
    delete dbRecord['has_unacknowledged_changes'];
    delete dbRecord['post_publish_changes'];
    delete dbRecord['is_published'];
    delete dbRecord['version'];
  }
  // prioritization_sessions: omit columns not in DB schema (isolated_items added in migration 207)
  if (tableName === 'prioritization_sessions') {
    delete dbRecord['session_type'];
    // Retire columns (mig 758) move only through retire_session /
    // restore_retired_session; a DB gate refuses any client write to them.
    delete dbRecord['retired_at'];
    delete dbRecord['retired_by'];
    delete dbRecord['retired_reason'];
    // isolated_items: omit only if column doesn't exist (pre-migration 207); remove this omit after applying migration 207
    if (!dbRecord['isolated_items']) {
      delete dbRecord['isolated_items'];
    }
    delete dbRecord['meeting_link'];
    delete dbRecord['meeting_provider'];
    // deliberation_record_id is a TS-only enrichment field; the relationship is
    // held by deliberation_records.session_id (FK to this table), not a column here.
    delete dbRecord['deliberation_record_id'];
    // The run_mode column is mode_config's run mode, by the one rule
    // (runModeOfSavedConfig; the column trigger, migration 1045, applies the same).
    promoteSessionRunMode(dbRecord, 'insert');
  }
  // value_proposals: rename proposed_value→value and strip TS-only fields; no workspace_id column in DB.
  // value_kind is a real DB column (migration 233) and is intentionally preserved here.
  // rationale and round_id are real DB columns (migration 262) and are intentionally preserved.
  if (tableName === 'value_proposals') {
    if ('proposed_value' in dbRecord) {
      dbRecord['value'] = dbRecord['proposed_value'];
      delete dbRecord['proposed_value'];
    }
    delete dbRecord['cycle_id'];
    delete dbRecord['proposer_role'];
    delete dbRecord['evidence'];
    delete dbRecord['reviewed_by'];
    delete dbRecord['reviewed_at'];
    delete dbRecord['review_note'];
    delete dbRecord['workspace_id'];
  }
  // accepted_values: no workspace_id column in DB.
  // value_kind, contributor_count, aggregation_method, aggregation_inputs are real DB columns
  // (migration 233) and are intentionally preserved here.
  if (tableName === 'accepted_values') {
    delete dbRecord['workspace_id'];
  }
  // item_clusters: members live in cluster_memberships; omit member_item_ids; table has no workspace_id
  if (tableName === 'item_clusters') {
    delete dbRecord['member_item_ids'];
    delete dbRecord['workspace_id'];
  }
  // cycle_attribute_configs has no workspace_id column; do not add it
  if (tableName === 'cycle_attribute_configs') {
    delete dbRecord['workspace_id'];
  }
  // item_relationship_groups: column is group_type, not type
  if (tableName === 'item_relationship_groups') {
    if (dbRecord['type'] !== undefined) {
      dbRecord['group_type'] = dbRecord['type'];
      delete dbRecord['type'];
    }
  }
  // item_relationship_edges: column is relationship_type, not type
  if (tableName === 'item_relationship_edges') {
    if (dbRecord['type'] !== undefined) {
      dbRecord['relationship_type'] = dbRecord['type'];
      delete dbRecord['type'];
    }
  }
  // org_units: strip TS-only fields not present in the DB schema
  if (tableName === 'org_units') {
    delete dbRecord['cost_center'];
    delete dbRecord['location'];
    delete dbRecord['description'];
    delete dbRecord['sync_source'];
    delete dbRecord['last_synced_at'];
    delete dbRecord['queue_settings'];
  }
  if (tableName === 'degap_cycles') {
    normalizeDegapCycleRecord(dbRecord);
    stripCyclePolicyKeysFromMetadata(dbRecord);
  }
  if (tableName === 'workspaces') {
    stripWorkspacePolicyColumns(dbRecord);
  }
  // Auto-add workspace_id when available. For degap_cycles/workflows, RLS allows insert when
  // workspace_id is in user_workspace_ids(auth.uid()), or is_super_admin, or workspace_id IS NULL.
  // Only add when key is missing (undefined); do not overwrite explicit null (e.g. demo workflows).
  // value_proposals and accepted_values have no workspace_id column in DB.
  const workspaceId = getCurrentWorkspaceId();
  const skipWorkspaceId = tableName === 'cycle_attribute_configs' || tableName === 'value_proposals' || tableName === 'accepted_values' || tableName === 'item_clusters' || tableName === 'input_round_items' || tableName === 'input_round_participants';
  if (workspaceId && dbRecord['workspace_id'] === undefined && !skipWorkspaceId) {
    dbRecord['workspace_id'] = workspaceId;
    if (isInDemoMode()) {
      console.log(`[SupabaseSync] Auto-adding workspace_id ${workspaceId} to ${tableName} insert`);
    }
  }
  return dbRecord;
}

/**
 * Insert a new record
 */
export async function insertRecord<T extends { id?: string }>(
  tableName: string,
  record: T
): Promise<SyncResult<T>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const dbRecord = toInsertRow(tableName, record);
    
    const { data, error } = await supabase
      .from(tableName)
      .insert(dbRecord)
      .select()
      .single();
    
    if (error) {
      console.error(`[SupabaseSync] Error inserting into ${tableName}:`, error);
      return { data: null, error: error.message };
    }

    notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: fromDbFormat<T>(data as Record<string, unknown>), error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception inserting into ${tableName}:`, error);
    return { data: null, error: message };
  }
}

/**
 * Update an existing record
 */
export type UpdateRecordOptions = {
  /**
   * For `degap_cycles` only: include `status` in the PATCH.
   * Default false — full-object syncs often carry stale `draft` and must not overwrite
   * RPC-authoritative lifecycle status (see transition_cycle_phase).
   */
  includeDegapCycleStatus?: boolean;
};

export async function updateRecord<T>(
  tableName: string,
  id: string,
  updates: Partial<T>,
  options?: UpdateRecordOptions
): Promise<SyncResult<T>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const dbUpdates = toDbFormat(updates as Record<string, unknown>);
    if (tableName === 'item_pools') {
      delete dbUpdates['comparison_set_id'];
      delete dbUpdates['criteria_ids'];
      delete dbUpdates['has_unacknowledged_changes'];
      delete dbUpdates['post_publish_changes'];
      delete dbUpdates['is_published'];
      delete dbUpdates['version'];
      // RPC-only (mig 760, set_item_pool_cut_line): never echo it back on a
      // whole-row update — a stale local value would be refused by the gate.
      delete dbUpdates['cut_line_position'];
      // RPC-only too (mig 749, set_item_pool_lock). The local copy of the lock
      // time is a JS Date, so it comes back a few microseconds off the stored
      // one; the lock gate reads that as a lock change and refuses the whole
      // row — every reorder, rename or tag edit of a locked pool failed.
      delete dbUpdates['pool_locked_at'];
      delete dbUpdates['pool_locked_by'];
    }
    if (tableName === 'item_clusters') {
      delete dbUpdates['member_item_ids'];
    }
    // prioritization_sessions: omit columns not in DB schema (add isolated_items migration when ready)
    if (tableName === 'prioritization_sessions') {
      delete dbUpdates['session_type'];
      // Retire columns (mig 758): RPC-only — never echo them back on an update.
      delete dbUpdates['retired_at'];
      delete dbUpdates['retired_by'];
      delete dbUpdates['retired_reason'];
      delete dbUpdates['isolated_items'];
      delete dbUpdates['meeting_link'];
      delete dbUpdates['meeting_provider'];
      // Frontend-only or computed fields that have no DB column:
      delete dbUpdates['status'];
      delete dbUpdates['eligibility_items'];
      delete dbUpdates['eligibility_status'];
      delete dbUpdates['item_eligibility_cache'];
      delete dbUpdates['presence'];
      delete dbUpdates['tool_compatibility'];
      // Fields added by local enrichment / joins — not DB columns:
      delete dbUpdates['deliberation_record_id'];
      // A loaded session carries the column back as `runMode`; mode_config's
      // run mode is the one the app reads and changes, so it wins.
      promoteSessionRunMode(dbUpdates, 'update');
    }
    if (tableName === 'org_units') {
      delete dbUpdates['cost_center'];
      delete dbUpdates['location'];
      delete dbUpdates['sync_source'];
      delete dbUpdates['last_synced_at'];
      // Keep queue_settings so queue settings (enabled, acceptsSubmissions) are persisted
    }
    // value_proposals: rename proposed_value→value and strip TS-only fields; no workspace_id column in DB.
    // value_kind is a real DB column (migration 233) and is intentionally preserved here.
    // rationale and round_id are real DB columns (migration 262) and are intentionally preserved.
    if (tableName === 'value_proposals') {
      if ('proposed_value' in dbUpdates) {
        dbUpdates['value'] = dbUpdates['proposed_value'];
        delete dbUpdates['proposed_value'];
      }
      delete dbUpdates['cycle_id'];
      delete dbUpdates['proposer_role'];
      delete dbUpdates['evidence'];
      delete dbUpdates['reviewed_by'];
      delete dbUpdates['reviewed_at'];
      delete dbUpdates['review_note'];
      delete dbUpdates['workspace_id'];
    }
    // accepted_values: no workspace_id column in DB.
    // value_kind, contributor_count, aggregation_method, aggregation_inputs are real DB columns
    // (migration 233) and are intentionally preserved here.
    if (tableName === 'accepted_values') {
      delete dbUpdates['workspace_id'];
    }
    if (tableName === 'degap_cycles') {
      normalizeDegapCycleRecord(dbUpdates);
      stripCyclePolicyKeysFromMetadata(dbUpdates);
      if (!options?.includeDegapCycleStatus) {
        delete dbUpdates['status'];
      }
    }
    if (tableName === 'workspaces') {
      stripWorkspacePolicyColumns(dbUpdates);
    }
    // item_relationship_groups: DB column is group_type, not type
    if (tableName === 'item_relationship_groups') {
      if (dbUpdates['type'] !== undefined) {
        dbUpdates['group_type'] = dbUpdates['type'];
        delete dbUpdates['type'];
      }
    }
    // item_relationship_edges: DB column is relationship_type, not type
    if (tableName === 'item_relationship_edges') {
      if (dbUpdates['type'] !== undefined) {
        dbUpdates['relationship_type'] = dbUpdates['type'];
        delete dbUpdates['type'];
      }
    }
    // items: strip frontend-only fields that have no DB column
    if (tableName === 'items') {
      delete dbUpdates['canonical_frame_id'];
      delete dbUpdates['frames'];
    }
    const { data, error } = await supabase
      .from(tableName)
      .update(dbUpdates)
      .eq('id', id)
      .select();
    const single = Array.isArray(data) ? data[0] : data;

    if (error) {
      console.error(`[SupabaseSync] Error updating ${tableName}:`, error);
      return { data: null, error: error.message };
    }
    if (single == null) {
      const msg = tableName === 'item_pools'
        ? 'Row not found or no access (pool may not exist in DB yet)'
        : 'Update returned no rows';
      console.warn(`[SupabaseSync] ${tableName} update: ${msg}`);
      return { data: null, error: msg };
    }

    notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: fromDbFormat<T>(single as Record<string, unknown>), error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception updating ${tableName}:`, error);
    return { data: null, error: message };
  }
}

/**
 * Delete a record.
 * Uses .select('id') so we can detect when RLS or missing row causes 0 rows
 * deleted (Supabase returns 204 with no error in that case).
 */
export async function deleteRecord(
  tableName: string,
  id: string
): Promise<SyncResult<boolean>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const { data, error } = await supabase
      .from(tableName)
      .delete()
      .eq('id', id)
      .select('id');
    
    if (error) {
      console.error(`[SupabaseSync] Error deleting from ${tableName}:`, error);
      return { data: false, error: error.message };
    }
    
    if (!data || data.length === 0) {
      const msg = 'Row not found or delete not allowed (check RLS or permissions).';
      console.error(`[SupabaseSync] Delete from ${tableName} affected 0 rows for id=${id}`);
      return { data: false, error: msg };
    }

    notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: true, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception deleting from ${tableName}:`, error);
    return { data: false, error: message };
  }
}

/**
 * Upsert a record (insert or update)
 */
export async function upsertRecord<T extends { id: string }>(
  tableName: string,
  record: T
): Promise<SyncResult<T>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const dbRecord = toDbFormat(record as Record<string, unknown>);
    
    const { data, error } = await supabase
      .from(tableName)
      .upsert(dbRecord)
      .select()
      .single();
    
    if (error) {
      console.error(`[SupabaseSync] Error upserting into ${tableName}:`, error);
      return { data: null, error: error.message };
    }

    notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: fromDbFormat<T>(data as Record<string, unknown>), error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception upserting into ${tableName}:`, error);
    return { data: null, error: message };
  }
}

// ============================================================================
// Batch Operations
// ============================================================================

/**
 * Insert multiple records
 */
export async function insertMany<T extends { id?: string }>(
  tableName: string,
  records: T[]
): Promise<SyncResult<T[]>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const dbRecords = records.map(r => toDbFormat(r as Record<string, unknown>));
    
    const { data, error } = await supabase
      .from(tableName)
      .insert(dbRecords)
      .select();
    
    if (error) {
      console.error(`[SupabaseSync] Error batch inserting into ${tableName}:`, error);
      return { data: null, error: error.message };
    }

    const transformed = (data || []).map(row => fromDbFormat<T>(row as Record<string, unknown>));
    if (transformed.length > 0) notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: transformed, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception batch inserting into ${tableName}:`, error);
    return { data: null, error: message };
  }
}

/**
 * Upsert multiple records
 */
export async function upsertMany<T extends { id: string }>(
  tableName: string,
  records: T[]
): Promise<SyncResult<T[]>> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    return { data: null, error: 'Supabase not configured' };
  }

  try {
    const dbRecords = records.map(r => toDbFormat(r as Record<string, unknown>));
    
    const { data, error } = await supabase
      .from(tableName)
      .upsert(dbRecords)
      .select();
    
    if (error) {
      console.error(`[SupabaseSync] Error batch upserting into ${tableName}:`, error);
      return { data: null, error: error.message };
    }

    const transformed = (data || []).map(row => fromDbFormat<T>(row as Record<string, unknown>));
    if (transformed.length > 0) notifyPrioritizationDashboardRelevantWrite(tableName);
    return { data: transformed, error: null };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error(`[SupabaseSync] Exception batch upserting into ${tableName}:`, error);
    return { data: null, error: message };
  }
}

// ============================================================================
// Sync State Management
// ============================================================================

/**
 * Create initial sync state
 */
export function createInitialSyncState(): SyncState {
  return {
    isLoading: false,
    isSyncing: false,
    lastSyncAt: null,
    syncError: null,
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  };
}

/**
 * Create a sync status updater function
 */
export function createSyncStateUpdater(
  set: (
    partial:
      | Partial<{ syncState: SyncState }>
      | ((state: { syncState: SyncState }) => Partial<{ syncState: SyncState }>)
  ) => void
) {
  return (updates: Partial<SyncState>) => {
    set((state) => ({
      syncState: { ...state.syncState, ...updates },
    }));
  };
}

// ============================================================================
// Group Expansion
// ============================================================================

/**
 * Check if an ID is a group ID (prefixed with "group:")
 */
export function isGroupId(id: string): boolean {
  return id.startsWith('group:');
}

/**
 * Extract the actual group ID from a prefixed group ID
 */
export function extractGroupId(prefixedId: string): string {
  return prefixedId.replace('group:', '');
}

/**
 * Fetch group members from Supabase
 */
export async function fetchGroupMembers(groupId: string): Promise<string[]> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) {
    console.warn('[GroupExpansion] Supabase not available');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from('memberships')
      .select('user_id')
      .eq('group_id', groupId);

    if (error) {
      console.error('[GroupExpansion] Error fetching group members:', error);
      return [];
    }

    return (data || []).map(m => m.user_id);
  } catch (err) {
    console.error('[GroupExpansion] Exception fetching group members:', err);
    return [];
  }
}

/**
 * Expand a list of participant IDs that may include groups to individual user IDs.
 * 
 * - If an ID is prefixed with "group:", fetch the group's members
 * - Regular user IDs are passed through
 * - Returns a deduplicated list of user IDs
 */
export async function expandGroupsToUsers(participantIds: string[]): Promise<string[]> {
  const userIds = new Set<string>();

  for (const id of participantIds) {
    if (isGroupId(id)) {
      // It's a group - expand to members
      const groupId = extractGroupId(id);
      const members = await fetchGroupMembers(groupId);
      members.forEach(userId => userIds.add(userId));
    } else {
      // It's a user ID - add directly
      userIds.add(id);
    }
  }

  return Array.from(userIds);
}
